const POSITION = {
  FIRST: 'first',
  MIDDLE: 'middle',
  LAST: 'last',
  ONLY: 'only',
} as const;

type Position = (typeof POSITION)[keyof typeof POSITION];
type CellStyle = string | { withSub: string; default: string };
type CellStylesByPosition = Partial<Record<Position, CellStyle>>;

const getPosition = (index: number, length: number): Position => {
  if (length === 1) return POSITION.ONLY;
  if (index === 0) return POSITION.FIRST;
  if (index === length - 1) return POSITION.LAST;

  return POSITION.MIDDLE;
};

const CELL_STYLES: Partial<Record<Position, CellStylesByPosition>> = {
  [POSITION.ONLY]: {
    [POSITION.FIRST]: {
      withSub: 'border border-b-0 border-r-0 rounded-tl-md',
      default: 'border border-r-0 rounded-tl-md rounded-bl-md',
    },
    [POSITION.LAST]: {
      withSub: 'border border-b-0 border-l-0 rounded-tr-md',
      default: 'border border-l-0 rounded-tr-md rounded-br-md',
    },
    [POSITION.MIDDLE]: {
      withSub: 'border-b-0 border-x-0',
      default: 'border-b border-navy-200 border-x-0',
    },
  },
  [POSITION.FIRST]: {
    [POSITION.FIRST]: 'border-r-0 border-b-0 rounded-tl-md',
    [POSITION.LAST]: 'border-l-0 border-b-0 rounded-tr-md',
    [POSITION.MIDDLE]: 'border-x-0 border-b-0',
  },
  [POSITION.MIDDLE]: {
    [POSITION.FIRST]: 'border-r-0 border-b-0',
    [POSITION.LAST]: 'border-l-0 border-b-0',
    [POSITION.MIDDLE]: 'border-x-0 border-b-0',
  },
  [POSITION.LAST]: {
    [POSITION.FIRST]: {
      withSub: 'border-r-0 border-b-0',
      default: 'border-r-0 rounded-bl-md',
    },
    [POSITION.LAST]: {
      withSub: 'border-l-0 border-b-0',
      default: 'border-l-0 rounded-br-md',
    },
    [POSITION.MIDDLE]: {
      withSub: 'border-x-0 border-b-0',
      default: 'border-x-0 border-b-0 !border-b',
    },
  },
};

export const getExpandedRowStyles = (
  rowInd: number,
  cellInd: number,
  rowsLength: number,
  lastCellInd: number,
  hasSubComponent: boolean,
): string => {
  const rowPosition = getPosition(rowInd, rowsLength);
  const cellPosition = getPosition(cellInd, lastCellInd + 1);

  const rowStyles = CELL_STYLES[rowPosition];

  if (!rowStyles) {
    return 'border-x-0 border-b-0';
  }

  const cellStyles = rowStyles[cellPosition];

  if (!cellStyles) {
    return 'border-x-0 border-b-0';
  }

  if (typeof cellStyles === 'string') {
    return cellStyles;
  }

  return hasSubComponent ? cellStyles.withSub : cellStyles.default;
};
